import { useCallback, useEffect, useState } from "react"
import { Client } from "@xmpp/client"
import AdHocCommands, { RouteAlertConfig } from "../api/adHocCommands"
import XmppPubSubApi from "../api/xmppPubSubApi"
import { useAppDispatch, useAppSelector } from "../reducers/hooks"
import {
  findRouteAlertConfig,
  updateRouteAlertConfig as updateRouteAlertConfigAction,
} from "../reducers/routes2gSlice"

/**
 * Hook to manage route alert configuration for a driver
 * Uses Redux store as primary source and only fetches from server if data is missing
 */
const useRouteAlertConfig = (
  client: Client | undefined,
  driverJid: string | null | undefined,
) => {
  const dispatch = useAppDispatch()
  const routeAlertConfig = useAppSelector(
    findRouteAlertConfig(driverJid || undefined),
  )

  // Only start in loading state if we don't have data in Redux
  const [isLoading, setIsLoading] = useState(!routeAlertConfig)
  const [error, setError] = useState<Error | null>(null)

  const fetchRouteAlertConfig = useCallback(async () => {
    if (!client || !driverJid) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const config = await XmppPubSubApi.fetchRouteAlertConfig(
        client,
        driverJid,
      )
      if (config) {
        dispatch(updateRouteAlertConfigAction({ jid: driverJid, config }))
      }
      setIsLoading(false)
    } catch (err) {
      console.error("Error fetching route alert config:", err)
      setError(err instanceof Error ? err : new Error(String(err)))
      setIsLoading(false)
    }
  }, [client, driverJid, dispatch])

  const updateAlertConfig = useCallback(
    async (config: Partial<RouteAlertConfig>) => {
      if (!client || !driverJid) return

      setIsLoading(true)
      setError(null)

      try {
        // Send update to server first
        await AdHocCommands.updateRouteAlertConfig(client, driverJid, config)

        // Then update Redux state with the saved config
        dispatch(updateRouteAlertConfigAction({ jid: driverJid, config }))
        setIsLoading(false)
      } catch (err) {
        console.error("Error updating route alert config:", err)
        setError(err instanceof Error ? err : new Error(String(err)))
        // Fetch current state from server on error
        await fetchRouteAlertConfig()
      }
    },
    [client, driverJid, fetchRouteAlertConfig, dispatch],
  )

  useEffect(() => {
    // Only fetch if we don't have data in Redux yet
    if (!routeAlertConfig && client && driverJid) {
      fetchRouteAlertConfig()
    } else if (routeAlertConfig) {
      // If we have data in Redux, we're not loading
      setIsLoading(false)
    }
  }, [routeAlertConfig, client, driverJid, fetchRouteAlertConfig])

  return {
    routeAlertConfig,
    isLoading,
    error,
    updateAlertConfig,
    refreshRouteAlertConfig: fetchRouteAlertConfig,
  }
}

export default useRouteAlertConfig
